import { Popconfirm, Tag, Select } from 'antd';
import Loader from 'components/loader';
import usePublications from "hooks/usePublications";
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { estadoPublicacion, routesNames } from "utils/Constants";
import { jornadas, states, turnos } from "utils/Tables";
import { formatDateFull, getNameFromObject } from "utils/Utils";
import PostulatedsTable from './components/PostulatedsTable';
import ModalGraphicResources from './components/ModalGraphicResources';
import { getAllEspecialidades } from 'services/Especialidades';
import { setChangeStatus, getEstadosPublicaciones } from 'services/Estados';
import { InstagramOutlined } from "@ant-design/icons";
import { getSiteUrl } from "config/config"
import SectionTitle from "components/sectionTitle";
import { Card, Row, ActionButton } from "components/detailView";
import {
    MdDescription,
    MdWork,
    MdLocationOn,
    MdFlag,
    MdImage,
    MdBusiness,
    MdBolt,
} from "react-icons/md";

const PUBLIC_URI = process.env.REACT_APP_PUBLIC_URI_IMAGES;

export default function Publication() {
    const { isLoading, response, fetchData } = usePublications()
    const [especialidades, setEspecialidades] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [type, setType] = useState(null)
    const [estadoPub, setEstadoPub] = useState(response?.estado_id || null)
    const [estados, setEstados] = useState([])

    let { id } = useParams();

    useEffect(() => {
        fetchData(id)
        getEspecialidades()
        getEstados()
    }, [])

    useEffect(() => {
        setEstadoPub(response?.estado_id)
    }, [response])

    const getEspecialidades = async () => {
        const data = await getAllEspecialidades()
        setEspecialidades(data)
    }

    const getEstados = async () => {
        const data = await getEstadosPublicaciones()
        const estados = data?.map(item => ({
            value: item.id,
            label: item.nombre
        }));

        setEstados(estados)
    }

    const resources = [
        {
            value: 'feed',
            label: <p className='font-semibold'><InstagramOutlined className='mr-2' /> IG Feed (1080 x 1350px)</p>,
        },
        {
            value: 'story',
            label: <p className='font-semibold'><InstagramOutlined className='mr-2' /> IG Story (1080 x 1920px)</p>,
        },
    ]

    const handleSelect = (value) => {
        setEstadoPub(value)
    }

    const handleSelectType = (value) => {
        setIsModalOpen(true)
        setType(value)
    }

    if (isLoading) {
        return (
            <div className="mt-10 flex items-center justify-center">
                <Loader />
            </div>
        )
    }

    if (!response) return null

    return (
        <div className="w-full">
            <div className='mb-5 flex flex-wrap items-center gap-2'>
                <h2 className="text-xl font-bold text-navy-700 dark:text-white">{response?.nombre}</h2>
                {response?.estado_publicacion && (
                    <Tag color={`${response.estado_publicacion.color}`}>{response.estado_publicacion.nombre}</Tag>
                )}
                {response?.privada == 1 && <Tag color="gold">Confidencial</Tag>}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-4">
                <div className="flex flex-col gap-5 xl:col-span-3">
                    <SectionTitle eyebrow="Detalle">Datos de la publicación</SectionTitle>

                    <Card title="Institución" icon={<MdBusiness size={20} />}>
                        <div className="sm:col-span-2 flex items-center gap-3">
                            {response?.cuenta?.logo_url && (
                                <img
                                    className="h-12 w-12 shrink-0 object-contain"
                                    src={`${PUBLIC_URI}uploads/${response.cuenta.logo_url}`}
                                    alt=""
                                />
                            )}
                            <div>
                                <p className="text-sm font-bold text-navy-700 dark:text-white">{response?.cuenta?.nombre}</p>
                                {response.cuenta?.user?.plan?.detalle?.id == 1 ? (
                                    <Tag color="gold">GOLD</Tag>
                                ) : (
                                    <Tag color="purple">STANDARD</Tag>
                                )}
                            </div>
                        </div>

                        <Row label="Teléfono responsable" value={response.cuenta?.responsable_telefono} />
                        <Row label="Publicación creada" value={formatDateFull(response?.created_at)} />

                        <div className="sm:col-span-2 mt-2 flex flex-col gap-2 border-t border-gray-100 pt-4 dark:border-navy-700">
                            <Link
                                to={`${routesNames.USER}/${response.cuenta?.user_id}`}
                                className="text-sm font-semibold text-blue-700"
                            >
                                Ver perfil (admin)
                            </Link>
                        </div>
                    </Card>

                    <Card title="Descripción general" icon={<MdDescription size={20} />}>
                        <Row full label="Título" value={response?.nombre} />
                        <div
                            className="sm:col-span-2 whitespace-pre-line text-sm text-navy-700 dark:text-white"
                            dangerouslySetInnerHTML={{ __html: response.descripcion }}
                        />
                    </Card>

                    <Card title="Contratación y jornada" icon={<MdWork size={20} />}>
                        <Row label="Turno" value={getNameFromObject(turnos, response.turno)} />
                        <Row label="Tipo de jornada" value={getNameFromObject(jornadas, response.jornada)} />
                        <Row
                            label="Especialidad"
                            value={
                                response.especialidad_id === null
                                    ? "Sin especialidad"
                                    : getNameFromObject(especialidades, response.especialidad_id, 'especialidad', true)
                            }
                            full
                        />
                    </Card>

                    <Card title="Ubicación" icon={<MdLocationOn size={20} />}>
                        <Row label="Provincia" value={getNameFromObject(states, response.ubicacion?.provincia)} />
                        <Row label="Localidad" value={response.ubicacion?.localidad} />
                    </Card>
                </div>

                <div className="xl:col-span-1">
                    <div className="sticky top-[90px] mt-[60px] flex flex-col gap-5">
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                            <h3 className="mb-2 flex items-center gap-2 text-base font-bold text-navy-700 dark:text-white">
                                <MdBolt className="text-gray-600 dark:text-gray-300" size={20} /> Acciones
                            </h3>
                            <div className="flex flex-col gap-2">
                                <Link to={`${routesNames.PUBLICATION_EDIT}/${id}`}>
                                    <ActionButton color="navy">Editar publicación</ActionButton>
                                </Link>
                                <a href={getSiteUrl() + `publication/${response?.slug}`} target="_blank" rel="noreferrer">
                                    <ActionButton color="green">Ver publicación</ActionButton>
                                </a>
                            </div>
                        </div>

                        <Card title="Estado" icon={<MdFlag size={20} />}>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                {response?.facturacion_sin_finalizar?.pago_confirmado === 1 && response.estado_id === estadoPublicacion.NO_APROBADA && (
                                    <Tag color='red' className='text-center'>Publicación rechazada</Tag>
                                )}
                                <Select
                                    className='w-full'
                                    placeholder='Estado de publicación'
                                    value={estadoPub}
                                    onSelect={handleSelect}
                                    options={estados}
                                />
                                <Popconfirm
                                    title="Cambiar estado"
                                    description="¿Está seguro que desea cambiar el estado de la publicación?"
                                    onConfirm={async () => {
                                        await setChangeStatus(response.id, { estado: estadoPub })
                                        window.location.reload()
                                    }}
                                    okText="Si"
                                    cancelText="No"
                                    okButtonProps={{ className: "bg-blue-500" }}
                                >
                                    <button className='w-full rounded-md bg-aprobado px-4 py-1 text-sm text-white'>Cambiar estado</button>
                                </Popconfirm>
                            </div>
                        </Card>

                        <Card title="Generador de placas" icon={<MdImage size={20} />}>
                            <div className="sm:col-span-2 flex flex-col gap-2">
                                <Select
                                    className='w-full'
                                    placeholder='Tipo de recurso gráfico...'
                                    value={type}
                                    onSelect={handleSelectType}
                                    options={resources}
                                />
                                <ModalGraphicResources
                                    isModalOpen={isModalOpen}
                                    setIsModalOpen={setIsModalOpen}
                                    data={response}
                                    type={type}
                                    setType={setType}
                                />
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            <div className="mt-8">
                <SectionTitle eyebrow="Candidatos">Postulaciones</SectionTitle>
                <PostulatedsTable id={id} />
            </div>
        </div>
    )
}
